import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, Edit3, Lightbulb, FileText, Download, Settings, MousePointer, CheckCircle, XCircle, User, CreditCard, BookOpen, Zap, Target, Star } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled(motion.div)`
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#6a737d'};
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const Content = styled.div`
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  line-height: 1.6;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FeatureList = styled.div`
  display: grid;
  gap: 16px;
`;

const FeatureItem = styled.div`
  padding: 16px;
  background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};
`;

const FeatureHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const FeatureIcon = styled.div`
  color: ${props => props.color || '#0969da'};
`;

const FeatureName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin: 0;
`;

const FeatureDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#6a737d'};
  margin: 0 0 8px 0;
`;

const StepList = styled.ol`
  margin: 8px 0 0 0;
  padding-left: 20px;
`;

const StepItem = styled.li`
  font-size: 14px;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin-bottom: 4px;
`;

const TipBox = styled.div`
  background: ${props => props.theme.mode === 'dark' ? 'rgba(9, 105, 218, 0.1)' : 'rgba(9, 105, 218, 0.05)'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? 'rgba(9, 105, 218, 0.3)' : 'rgba(9, 105, 218, 0.2)'};
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
`;

const TipTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TipText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#6a737d'};
  margin: 0;
`;

const HelpModal = ({ isOpen, onClose }) => {
  const { theme } = useTheme();

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <Modal
            theme={theme}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header theme={theme}>
              <Title theme={theme}>
                <Lightbulb size={24} />
                AI 워드 에이전트 사용 가이드
              </Title>
              <CloseButton theme={theme} onClick={onClose}>
                <X size={20} />
              </CloseButton>
            </Header>

            <Content theme={theme}>
              <Section>
                <SectionTitle>
                  <MessageSquare size={18} />
                  기본 사용법
                </SectionTitle>
                <FeatureList>
                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#28a745">
                        <Edit3 size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>글 작성</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      AI에게 새로운 문서 작성을 요청합니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>1. 사이드바에서 "AI 글 작성" 모드 선택</StepItem>
                      <StepItem theme={theme}>2. 원하는 내용을 입력 (예: "연구 보고서를 작성해줘")</StepItem>
                      <StepItem theme={theme}>3. AI가 생성한 내용을 확인하고 "적용" 또는 "취소" 선택</StepItem>
                    </StepList>
                  </FeatureItem>

                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#ffc107">
                        <Edit3 size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>글 수정</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      기존 문서의 특정 부분을 수정하거나 개선합니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>1. 수정하고 싶은 텍스트를 드래그로 선택</StepItem>
                      <StepItem theme={theme}>2. 나타나는 "Add Chat" 버튼 클릭</StepItem>
                      <StepItem theme={theme}>3. 수정 요청 입력 (예: "이 부분을 더 자세히 써줘")</StepItem>
                      <StepItem theme={theme}>4. 미리보기를 확인하고 "적용" 또는 "취소" 선택</StepItem>
                      <StepItem theme={theme}>4. 만약 add chat 버튼이 나타나지 않으면 새 대화를 누른 후 수정하고 싶은 텍스트를 드래그로 선택하고 다시 시도해주세요.</StepItem>
                    </StepList>
                  </FeatureItem>

                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#17a2b8">
                        <MessageSquare size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>질문하기</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      문서 내용에 대해 질문하고 답변을 받습니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>1. 사이드바에서 "질문하기" 모드 선택</StepItem>
                      <StepItem theme={theme}>2. 궁금한 내용을 질문</StepItem>
                      <StepItem theme={theme}>3. AI의 답변을 확인</StepItem>
                    </StepList>
                  </FeatureItem>
                </FeatureList>
              </Section>

              <Section>
                <SectionTitle>
                  <MousePointer size={18} />
                  고급 기능
                </SectionTitle>
                <FeatureList>
                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#6f42c1">
                        <FileText size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>부분 수정 (Cursor 스타일)</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      텍스트를 선택하면 해당 부분만 정확히 수정됩니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>1. 수정할 텍스트를 마우스로 드래그</StepItem>
                      <StepItem theme={theme}>2. 드래그 끝에 나타나는 "Add Chat" 툴팁 클릭</StepItem>
                      <StepItem theme={theme}>3. 수정 요청 입력</StepItem>
                      <StepItem theme={theme}>4. 선택된 부분 아래에 미리보기 표시</StepItem>
                      <StepItem theme={theme}>5. "적용" 시 해당 부분만 교체, "취소" 시 원래대로</StepItem>
                    </StepList>
                  </FeatureItem>

                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#fd7e14">
                        <Download size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>자동 저장</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      AI가 생성한 모든 문서가 자동으로 최근 파일에 저장됩니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>1. AI가 새 문서를 생성하면 자동으로 저장</StepItem>
                      <StepItem theme={theme}>2. "최근 파일" 버튼에서 저장된 파일들 확인</StepItem>
                      <StepItem theme={theme}>3. 파일 클릭으로 내용 불러오기 가능</StepItem>
                    </StepList>
                  </FeatureItem>
                </FeatureList>
              </Section>

              <Section>
                <SectionTitle>
                  <Star size={18} />
                  새로운 기능들
                </SectionTitle>
                <FeatureList>
                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#7c3aed">
                        <BookOpen size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>논문 모드 (Reasoner)</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      전문적인 논문과 연구 보고서를 2단계로 나누어 생성합니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>1. AI 모델을 "논문 모드"로 선택</StepItem>
                      <StepItem theme={theme}>2. 주제 입력 시 자동으로 2단계 생성 시작</StepItem>
                      <StepItem theme={theme}>3. 1단계: Abstract, Introduction, Method 작성</StepItem>
                      <StepItem theme={theme}>4. 2단계: Experiments, Results, Conclusion 작성</StepItem>
                      <StepItem theme={theme}>5. 진행 상황을 실시간으로 확인 가능</StepItem>
                    </StepList>
                  </FeatureItem>

                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#10b981">
                        <User size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>계정 시스템</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      로그인/회원가입으로 포인트 관리와 프로필 커스터마이징이 가능합니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>1. 게스트로 1500포인트 무료 체험</StepItem>
                      <StepItem theme={theme}>2. 회원가입으로 추가 1500포인트 획득</StepItem>
                      <StepItem theme={theme}>3. 아바타와 이름 커스터마이징</StepItem>
                      <StepItem theme={theme}>4. 포인트 충전으로 더 많은 AI 기능 이용</StepItem>
                    </StepList>
                  </FeatureItem>

                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#f59e0b">
                        <CreditCard size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>포인트 시스템</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      AI 기능 사용에 따른 포인트 차감 시스템으로 공정한 이용이 가능합니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>• 일반 모드: 300원/회 (짧은 글, 아이디어 확장)</StepItem>
                      <StepItem theme={theme}>• 논문 모드: 500원/회 (장문 논문, 수식/표 포함)</StepItem>
                      <StepItem theme={theme}>• 수정/질문: 200원/회</StepItem>
                      <StepItem theme={theme}>• 포인트 잔액은 사이드바에서 실시간 확인</StepItem>
                    </StepList>
                  </FeatureItem>

                  <FeatureItem theme={theme}>
                    <FeatureHeader>
                      <FeatureIcon color="#ef4444">
                        <Zap size={16} />
                      </FeatureIcon>
                      <FeatureName theme={theme}>진행 표시 시스템</FeatureName>
                    </FeatureHeader>
                    <FeatureDescription theme={theme}>
                      논문 모드 사용 시 실시간 진행 상황을 시각적으로 확인할 수 있습니다.
                    </FeatureDescription>
                    <StepList>
                      <StepItem theme={theme}>• 단계별 배지로 현재 진행 상황 표시</StepItem>
                      <StepItem theme={theme}>• 진행률 바로 전체 진행도 확인</StepItem>
                      <StepItem theme={theme}>• 완료 체크마크로 단계별 완료 상태 표시</StepItem>
                      <StepItem theme={theme}>• 실시간 메시지로 현재 작업 내용 안내</StepItem>
                    </StepList>
                  </FeatureItem>
                </FeatureList>
              </Section>

              <Section>
                <SectionTitle>
                  <Settings size={18} />
                  설정 및 팁
                </SectionTitle>
                
                <TipBox theme={theme}>
                  <TipTitle theme={theme}>
                    <Lightbulb size={14} />
                    AI 모드 및 언어 선택
                  </TipTitle>
                  <TipText theme={theme}>
                    더 정확한 AI 응답을 위해 AI 모드 및 언어 선택을 확인하세요. 
                    설정하지 않으면 기본 모드와 한국어로 응답을 제공합니다.
                  </TipText>
                </TipBox>

                <TipBox theme={theme}>
                  <TipTitle theme={theme}>
                    <CheckCircle size={14} />
                    효과적인 사용법
                  </TipTitle>
                  <TipText theme={theme}>
                    • 구체적인 요청을 하세요 (예: "더 자세히" → "구체적인 예시와 함께 더 자세히")<br/>
                    • 긴 문서는 여러 부분으로 나누어 수정하세요<br/>
                    • 질문하기 기능으로 문서 내용을 검증하세요
                  </TipText>
                </TipBox>

                <TipBox theme={theme}>
                  <TipTitle theme={theme}>
                    <XCircle size={14} />
                    주의사항
                  </TipTitle>
                  <TipText theme={theme}>
                    • 중요한 문서는 수동으로도 저장하세요<br/>
                    • AI 생성 내용은 검토 후 사용하세요<br/>
                    • 브라우저를 닫기 전에 작업 내용을 확인하세요
                  </TipText>
                </TipBox>
              </Section>
            </Content>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default HelpModal;
